import { useApiUtils } from '@core/util/apiUtils';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Collapse, Divider, IconButton, ListItem, Tooltip, Typography } from '@material-ui/core';
import { ConfiguracaoContext } from 'app/main/configuracao/ConfiguracaoContext';
import { errorAlert, excluirAlert } from 'app/services/alerts';
import { categoriaApi, categoriaClasseApi } from 'app/services/api/planoContasApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import React, { useCallback, useContext, useState } from 'react';
import { FaPencilAlt, FaPlusSquare, FaTrash } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { FormClasseAdd, FormClasseEdit } from '..';
import { CategoriaContext } from '../../CategoriaContext';
import categoriaStyles from './styles';

const CATEGORIA = 'Categoria';
const TIPOS_CLASSE = {
  SINTETICA: 'Sintético',
  ANALITICA: 'Analítico',
};

const CategoriaItem = ({ classe, onAdd, onRemove, onEdit }) => {
  const [, setClassesReceita] = useContext(CategoriaContext).classesReceita;
  const [, setClassesDespesa] = useContext(CategoriaContext).classesDespesa;
  const [, setPlanoContasId] = useContext(CategoriaContext).planoContasId;
  const [, setVinculosAutomaticos] = useContext(CategoriaContext).vinculosAutomaticos;
  const [, setPlanoContasUtilizado] = useContext(CategoriaContext).planoContasUtilizado;
  const [isEditing, setEditing] = useState(false);
  const [isAdding, setAdding] = useState(false);
  const [readOnly] = useContext(ConfiguracaoContext).categoriaReadOnly;
  const [centroCustoReadOnly] = useContext(ConfiguracaoContext).centroCustoReadOnly;
  const [, setLoading] = useContext(CategoriaContext).loading;

  const styles = categoriaStyles();
  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const [dreReceitaList] = useContext(CategoriaContext).dreReceitaList;
  const [dreDespesaList] = useContext(CategoriaContext).dreDespesaList;

  const tipoClasse = TIPOS_CLASSE[classe.tipoClasse];

  const handleOpenAddClass = useCallback(() => {
    setAdding((adding) => !adding);
    setEditing(false);
  }, []);

  const handleOpenEditClass = useCallback(() => {
    setEditing((editing) => !editing);
    setAdding(false);
  }, []);

  const handleCancel = () => {
    setAdding(false);
    setEditing(false);
  };

  /**
   * @param {string} action one of 'add' 'remove'
   */
  const handleSuccess = () => {
    setEditing(false);
    setAdding(false);
  };

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const carregarCategorias = async () => {
    setLoading(true);
    try {
      const response = await categoriaApi.getByEmpresa();

      if (response.data.classes) {
        setClassesReceita(response.data.classes.filter((classe) => classe.natureza === 'RECEITA'));
        setClassesDespesa(response.data.classes.filter((classe) => classe.natureza === 'DESPESA'));
      } else {
        setClassesReceita([]);
        setClassesDespesa([]);
      }

      setPlanoContasId(response.data.id);
      setPlanoContasUtilizado(response.data.utilizado);

      const vinculos = {};

      if (response.data.vinculosAutomaticos) {
        response.data.vinculosAutomaticos.forEach((v) => {
          vinculos[`${v.natureza}_${v.processo}`] = v;
        });
      }
      setVinculosAutomaticos(vinculos);
    } catch (error) {
      apiUtils.handleApiError(error);
    }

    setLoading(false);
  };

  const handleRemove = (e, classe) => {
    const message = classe?.classesFilhas?.length
      ? 'Somente categoria que não possui lançamento em suas subcategorias poderá ser excluída. Deseja excluir?'
      : 'Somente categoria que não possui lançamento poderá ser excluída. Deseja excluir?';

    excluirAlert({
      confirmar: async () => {
        try {
          const isReceita = classe?.natureza === 'RECEITA';
          eventReactGA(CATEGORIA, `Deletar categoria de ${isReceita ? 'receita' : 'despesa'}`);

          await categoriaClasseApi.delete(classe.id);

          await carregarCategorias();

          classe?.classesFilhas?.length
            ? showSuccess('Categorias excluídas com sucesso')
            : showSuccess('Categoria excluída com sucesso');
        } catch (err) {
          if (err.response.data.message.includes('utilizada')) {
            errorAlert({
              opcoes: {
                title: err.response.data.message,
              },
            });
          } else {
            apiUtils.handleApiError(err);
          }
        }
      },
      opcoes: {
        title: message,
        text: '',
      },
    });
  };

  const dreList = classe.natureza === 'RECEITA' ? dreReceitaList : dreDespesaList;

  const [_dre] = dreList.filter((d) => d.id === classe.dreId);

  return (
    <>
      <ListItem className={`${styles.listItem} h-52 ${styles[`nivel${classe.nivel}`]}`}>
        <Typography
          className={classe?.classesFilhas?.length ? styles.bold : 'bold'}
          variant="subtitle1"
          ml={50}
        >{`${classe.indice} ${classe.nome}`}</Typography>

        {_dre && (
          <>
            <Tooltip title={_dre.descricao} placement="bottom">
              <div className={styles.badge}>DRE</div>
            </Tooltip>
          </>
        )}

        {!readOnly && (
          <>
            {/* De acordo com a Keila só é necessário ter a permissão de editar */}
            {permissaoUtils.categoria().editar && (tipoClasse === TIPOS_CLASSE['SINTETICA'] || !classe?.utilizado) && (
              <Tooltip title="Adicionar subcategoria" placement="bottom">
                <IconButton onClick={handleOpenAddClass}>
                  <FaPlusSquare size={16} color="#5890aa" />
                </IconButton>
              </Tooltip>
            )}
            {/* De acordo com a Keila só é necessário ter a permissão de editar */}
            {permissaoUtils.categoria().editar && (
              <Tooltip title="Editar categoria" placement="bottom">
                <IconButton onClick={handleOpenEditClass}>
                  <FaPencilAlt size={14} color="#5890aa" />
                </IconButton>
              </Tooltip>
            )}
            {/* De acordo com a Keila só é necessário ter a permissão de editar */}
            {permissaoUtils.categoria().editar && (
              <Tooltip title="Excluir categoria" placement="bottom">
                <IconButton onClick={(e) => handleRemove(e, classe)}>
                  <FaTrash size={14} color="#5890aa" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}

        {!centroCustoReadOnly && (
          <>
            {/* De acordo com a Keila só é necessário ter a permissão de editar */}
            {permissaoUtils.centrocusto().editar && (tipoClasse === TIPOS_CLASSE['SINTETICA'] || !classe?.utilizado) && (
              <Tooltip title="Adicionar subcategoria" placement="bottom">
                <IconButton onClick={handleOpenAddClass}>
                  <FaPlusSquare size={16} color="#5890aa" />
                </IconButton>
              </Tooltip>
            )}
            {/* De acordo com a Keila só é necessário ter a permissão de editar */}
            {permissaoUtils.centrocusto().editar && (
              <Tooltip title="Editar categoria" placement="bottom">
                <IconButton onClick={handleOpenEditClass}>
                  <FaPencilAlt size={14} color="#5890aa" />
                </IconButton>
              </Tooltip>
            )}
            {/* De acordo com a Keila só é necessário ter a permissão de editar */}
            {permissaoUtils.centrocusto().editar && (
              <Tooltip title="Excluir categoria" placement="bottom">
                <IconButton onClick={(e) => handleRemove(e, classe)}>
                  <FaTrash size={14} color="#5890aa" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </ListItem>
      <Collapse in={isAdding}>
        {isAdding && (
          <FormClasseAdd
            classe={classe}
            dreList={dreList}
            onAdd={onAdd}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
          />
        )}
      </Collapse>
      <Collapse in={isEditing}>
        {isEditing && (
          <FormClasseEdit
            classe={classe}
            dreList={dreList}
            onCancel={handleCancel}
            onSubmit={onEdit}
            onSuccess={handleSuccess}
          />
        )}
      </Collapse>
      {classe.classesFilhas
        ? classe.classesFilhas
            .filter((classe) => !!classe)
            .map((filha) => (
              <React.Fragment key={filha.id}>
                <Divider />
                <CategoriaItem key={filha.id} classe={filha} onAdd={onAdd} onEdit={onEdit} onRemove={onRemove} />
              </React.Fragment>
            ))
        : ''}
    </>
  );
};

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.classe.id === nextProps.classe.id &&
    prevProps.classe.nome === nextProps.classe.nome &&
    prevProps.classe.dreId === nextProps.classe.dreId &&
    prevProps.isAdding === nextProps.isAdding &&
    prevProps.isEditing === nextProps.isEditing
  );
};

export default React.memo(CategoriaItem, areEqual);
